import React from "react";
import { useParams } from "react-router-dom";
import { tours } from "@data/tours";
import InteractiveTimeline from "@components/InteractiveTimeline";
import SEO from "@components/SEO";
import {
  createTourStructuredData,
  createBreadcrumbStructuredData,
} from "../utils/structuredData";

/** Per-region SEO copy. Title <= 60 chars, description 140-160 chars. */
const TOUR_META: Record<
  string,
  { title: string; description: string }
> = {
  "north-island": {
    title: "North Island New Zealand Tours | Ikhlas Tours",
    description:
      "North Island New Zealand tours covering Auckland, Hobbiton, Rotorua and Taupo. Customisable Muslim-friendly itineraries with local NZ guides — book from Malaysia.",
  },
  "south-island": {
    title: "South Island New Zealand Tours | Ikhlas Tours",
    description:
      "South Island New Zealand tours through Christchurch, Lake Tekapo, Mount Cook, Wanaka, Queenstown and Milford Sound. Muslim-friendly, customisable itineraries.",
  },
  "combo-island": {
    title: "New Zealand Tour Package Malaysia 2026 | Combo Island",
    description:
      "New Zealand tour package for Malaysia 2026 combining North and South Island highlights — Hobbiton, Rotorua, Mount Cook, Milford Sound and Queenstown.",
  },
};

const TourDetailPage: React.FC = () => {
  const { region } = useParams<{ region: string }>();

  const tour = tours.find((t) => t.region === region);

  if (!tour) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SEO
          title="Tour Not Found | Ikhlas Tours"
          description="The tour you're looking for doesn't exist or has been moved."
          path={`/tours/${region ?? ""}`}
          noIndex
        />
        <div className="text-center">
          <h1 className="text-4xl font-bold text-primaryText mb-4">
            Tour Not Found
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            The tour you're looking for doesn't exist or has been moved.
          </p>
          <a
            href="/tours"
            className="inline-block bg-primary text-primaryText px-6 py-3 rounded-md font-semibold hover:bg-opacity-90 transition-colors"
          >
            Back to Tours
          </a>
        </div>
      </div>
    );
  }

  const meta = TOUR_META[tour.region] ?? {
    title: `${tour.title} | Ikhlas Tours`,
    description: tour.description.slice(0, 155),
  };

  return (
    <div>
      <SEO
        title={meta.title}
        description={meta.description}
        path={`/tours/${tour.region}`}
        image={tour.featuredImage}
        type="article"
        structuredData={[
          createTourStructuredData(tour),
          createBreadcrumbStructuredData([
            { name: "Home", url: "/" },
            { name: "Tours", url: "/tours" },
            { name: tour.title, url: `/tours/${tour.region}` },
          ]),
        ]}
      />
      {/* Hero Section */}
      <section className="relative h-102">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat brightness-50"
          style={{
            backgroundImage: `url(${import.meta.env.VITE_CLOUDFRONT_URL}${
              tour.heroImage
            }`,
          }}
        />
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {tour.title}
            </h1>
            <div className="flex items-center justify-center space-x-6 text-lg">
              <span className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                {tour.duration} days
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Tour Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-primaryText mb-6">
            Tour Overview
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            {tour.description}
          </p>

          {/* Tour Highlights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="text-primary mb-2">
                <svg
                  className="w-8 h-8 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-primaryText mb-1">
                Multiple Destinations
              </h3>
              <p className="text-sm text-gray-600">
                Explore diverse landscapes across the island
              </p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="text-primary mb-2">
                <svg
                  className="w-8 h-8 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-primaryText mb-1">
                Expert Guides
              </h3>
              <p className="text-sm text-gray-600">
                Local knowledge and authentic experiences
              </p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="text-primary mb-2">
                <svg
                  className="w-8 h-8 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-primaryText mb-1">
                All Inclusive
              </h3>
              <p className="text-sm text-gray-600">
                Accommodation, transport, and selected meals
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Timeline */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primaryText mb-4">
              Daily Itinerary
            </h2>
            <p className="text-lg text-gray-600">
              Click on any day to see detailed activities and experiences
            </p>
          </div>

          <InteractiveTimeline itinerary={tour.itinerary} />
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-16 bg-secondary">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-primaryText mb-4">
            Ready to Book Your Adventure?
          </h2>
          <p className="text-xl text-primaryText mb-8">
            Join us on this incredible {tour.title} package
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="/contact"
              className="inline-block bg-primary text-white px-8 py-3 rounded-md font-semibold hover:bg-opacity-90 transition-all duration-300"
            >
              Contact Us Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TourDetailPage;
